import { EMR_PAGE_SOURCE_LABELS, type EmrPageSource } from "@emr/contracts";

/**
 * The page capture, as JavaScript source: `bobdocCapturePage(document, scope)`
 * copies what another EMR's page shows into one self-contained HTML string and
 * returns `{ html, frames, blockedFrames, shadowRoots }`.
 *
 * It runs inside the other EMR's page, from the "Send to BobDoc" bookmark, so
 * it is plain JavaScript with no imports and no closures over this module.
 * It copies the rendered page rather than its source: same-origin frames are
 * inlined, open shadow roots are flattened in display order, and typed form
 * values become attributes. What the page's styles hide is copied too (a lab
 * result's panel name, a folded section), marked `data-bobdoc-hidden`. Scripts,
 * styles, metadata, images and the values of hidden, password and file inputs
 * are left out, so session tokens and photos never leave that page.
 *
 * With a `scope` (see `CAPTURE_SCOPES`) it copies only the patient's chart and
 * that patient's open forms, never the lists of other patients around them,
 * and returns null when the page shows no chart. Nothing here sends anything
 * anywhere.
 */
export const PAGE_CAPTURE_SOURCE = String.raw`function bobdocCapturePage(page, scope) {
  const SKIP = new Set(["SCRIPT", "NOSCRIPT", "STYLE", "LINK", "META", "BASE", "TEMPLATE", "OBJECT", "EMBED", "CANVAS"]);
  const MEDIA = new Set(["IMG", "SOURCE", "VIDEO", "AUDIO", "TRACK", "IMAGE"]);
  const stats = { frames: 0, blockedFrames: 0, shadowRoots: 0 };
  const out = page.implementation.createHTMLDocument("");
  const styles = new Map();
  const styleOf = (element) => {
    if (!styles.has(element)) {
      let style = null;
      try {
        const view = element.ownerDocument && element.ownerDocument.defaultView;
        style = view ? view.getComputedStyle(element) : null;
      } catch (error) {
        style = null;
      }
      styles.set(element, style);
    }
    return styles.get(element);
  };
  const hidden = (element) => {
    const style = styleOf(element);
    return style !== null && style.display === "none";
  };
  // Whether the page shows this chart or form at all (a minimized dialog, another patient's
  // chart kept in the background); what a shown one hides inside is still copied.
  const shown = (element) => {
    for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
      if (hidden(node)) return false;
    }
    return true;
  };
  const copyChildren = (nodes, into) => {
    for (const child of Array.from(nodes)) {
      const copy = copyNode(child);
      if (copy) into.appendChild(copy);
    }
  };
  const copyNode = (node) => {
    if (node.nodeType === 3) return out.createTextNode(node.nodeValue);
    if (node.nodeType !== 1) return null;
    const tag = node.tagName.toUpperCase();
    if (SKIP.has(tag)) return null;
    if (tag === "IFRAME" || tag === "FRAME") {
      const holder = out.createElement("section");
      holder.setAttribute("data-bobdoc-frame", node.getAttribute("name") || node.id || "");
      let inner = null;
      try {
        inner = node.contentDocument;
      } catch (error) {
        inner = null;
      }
      if (inner && inner.documentElement) {
        stats.frames += 1;
        copyChildren((inner.body || inner.documentElement).childNodes, holder);
      } else {
        stats.blockedFrames += 1;
        holder.setAttribute("data-bobdoc-frame-blocked", "");
      }
      return holder;
    }
    if (tag === "FRAMESET") {
      const div = out.createElement("div");
      div.setAttribute("data-bobdoc-frameset", "");
      copyChildren(node.childNodes, div);
      return div;
    }
    if (tag === "SLOT" && typeof node.assignedNodes === "function") {
      const span = out.createElement("span");
      span.setAttribute("data-bobdoc-slot", node.getAttribute("name") || "");
      const assigned = node.assignedNodes({ flatten: true });
      copyChildren(assigned.length > 0 ? assigned : node.childNodes, span);
      return span;
    }
    const el = out.importNode(node, false);
    for (const attribute of Array.from(el.attributes)) {
      if (/^on/i.test(attribute.name) || attribute.name === "style") el.removeAttribute(attribute.name);
    }
    if (hidden(node)) el.setAttribute("data-bobdoc-hidden", "");
    if (MEDIA.has(tag)) {
      for (const name of ["src", "srcset", "href", "xlink:href", "poster"]) el.removeAttribute(name);
    }
    if (tag === "INPUT") {
      const type = (node.type || "").toLowerCase();
      if (type === "checkbox" || type === "radio") {
        if (node.checked) el.setAttribute("checked", "");
        else el.removeAttribute("checked");
      } else if (type === "password" || type === "hidden" || type === "file") {
        el.removeAttribute("value");
      } else {
        el.setAttribute("value", node.value);
      }
    } else if (tag === "OPTION") {
      if (node.selected) el.setAttribute("selected", "");
      else el.removeAttribute("selected");
    } else if (tag === "TEXTAREA") {
      el.textContent = node.value;
      return el;
    }
    if (node.shadowRoot) {
      stats.shadowRoots += 1;
      copyChildren(node.shadowRoot.childNodes, el);
    } else {
      copyChildren(node.childNodes, el);
    }
    return el;
  };
  let root;
  if (scope) {
    let chart = null;
    for (const selector of scope.chart) {
      chart = Array.from(page.querySelectorAll(selector)).find(shown) || null;
      if (chart) break;
    }
    if (!chart) return null;
    const roots = [chart];
    for (const form of scope.forms) {
      for (const element of Array.from(page.querySelectorAll(form.select))) {
        if (!element.querySelector(form.having) || !shown(element)) continue;
        if (roots.some((taken) => taken.contains(element) || element.contains(taken))) continue;
        roots.push(element);
      }
    }
    for (const taken of roots) {
      const copy = copyNode(taken);
      if (copy) out.body.appendChild(copy);
    }
    root = out.documentElement;
  } else {
    root = copyNode(page.documentElement);
  }
  return {
    html: "<!doctype html>\n" + root.outerHTML,
    frames: stats.frames,
    blockedFrames: stats.blockedFrames,
    shadowRoots: stats.shadowRoots,
  };
}`;

/**
 * What one send may carry of the patient's files. The bookmark leaves out what
 * goes past them, saying why, and BobDoc refuses a send that does not keep to
 * them.
 */
export const CAPTURED_FILE_LIMITS = {
  files: 100,
  fileBytes: 50 * 1024 * 1024,
  totalBytes: 200 * 1024 * 1024,
} as const;

/**
 * The bookmark's download of the patient's files from TELUS CHR, as JavaScript
 * source: `bobdocFiles(where, limits, progress)` resolves to `{ items, skipped }`
 * or, when CHR's list cannot be had, to `{ failed }`. It never rejects.
 *
 * CHR's Summary lists a patient's files without their contents, so this asks
 * CHR for them the way CHR's own page does: its file list for the patient in
 * the page's address, from the clinic's data host (`<clinic>.ca.inputhealth.com`
 * beside `<clinic>.inputhealth.com`), with the physician's own CHR session.
 * That list is CHR's private feed, not a published interface (seen October
 * 2026), so every field is read defensively. Only GET requests; nothing in CHR
 * changes. Each file keeps CHR's own filing details, never its download
 * address, which may itself grant access to the file.
 *
 * PDFs and JPEG or PNG images are downloaded, three at a time, and checked
 * against their type's signature, as BobDoc's document store checks them; any
 * other file, or one past `CAPTURED_FILE_LIMITS`, is listed in `skipped` with
 * the reason.
 */
export const CHR_FILES_SOURCE = String.raw`async function bobdocFiles(where, limits, progress) {
  const patient = /^#patients\/(\d+)(?:[/?]|$)/.exec(where.hash || "");
  if (!patient) return { failed: "The chart's address named no patient." };
  let api = where.origin;
  const clinic = /^https:\/\/([a-z0-9-]+)\.inputhealth\.com$/.exec(where.origin);
  if (clinic) {
    api = "https://" + clinic[1] + ".ca.inputhealth.com";
    // Where CHR itself asked for this patient's details, while the browser still lists it.
    try {
      for (const entry of performance.getEntriesByType("resource")) {
        const asked = /^(https:\/\/[a-z0-9.-]+\.inputhealth\.com)\/respondents\/(\d+)(?:[/?]|$)/.exec(entry.name);
        if (asked && asked[2] === patient[1]) {
          api = asked[1];
          break;
        }
      }
    } catch (error) {
      // No timing entries: the clinic's usual data host stands.
    }
  }
  const megabytes = (bytes) => Math.round(bytes / (1024 * 1024)) + " MB";
  const text = (value) => (typeof value === "string" && value.trim() !== "" ? value.trim() : null);
  const get = async (url, credentials, accept, ms) => {
    const stop = new AbortController();
    const timer = setTimeout(() => stop.abort(), ms);
    try {
      return await fetch(url, { credentials, headers: { accept }, signal: stop.signal });
    } finally {
      clearTimeout(timer);
    }
  };
  let listed = null;
  try {
    const response = await get(
      api + "/respondents/" + patient[1] + "/files/section-summary?resource_summary%5Bresource_type%5D=files&resource_summary%5Blatest_length%5D=10&resource_summary%5Ball%5D=true",
      "include",
      "application/json",
      30000,
    );
    if (!response.ok) return { failed: "CHR did not give the list of the patient's files (it answered " + response.status + ")." };
    const body = await response.json();
    listed = Array.isArray(body)
      ? body
      : [body && body.data, body && body.files, body && body.items, body && body.resources].find(Array.isArray) || null;
  } catch (error) {
    return { failed: "CHR's list of the patient's files could not be read." };
  }
  if (!listed) return { failed: "CHR's list of the patient's files was not in the form BobDoc knows." };
  const EXTENSIONS = { pdf: "application/pdf", jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png" };
  const typeOf = (entry) => {
    const said = String(entry.attachment_content_type || "").toLowerCase().split(";")[0].trim();
    if (said === "application/pdf" || said === "image/jpeg" || said === "image/png") return said;
    if (said === "image/jpg" || said === "image/pjpeg") return "image/jpeg";
    if (said !== "" && said !== "application/octet-stream" && said !== "binary/octet-stream") return null;
    const extension = /\.([a-z0-9]+)$/i.exec(String(entry.attachment_file_name || entry.name || ""));
    return (extension && EXTENSIONS[extension[1].toLowerCase()]) || null;
  };
  const signed = (type, bytes) => {
    const head = new Uint8Array(bytes, 0, Math.min(1024, bytes.byteLength));
    if (type === "application/pdf") return String.fromCharCode.apply(null, Array.from(head)).includes("%PDF-");
    if (type === "image/png") return [137, 80, 78, 71, 13, 10, 26, 10].every((byte, index) => head[index] === byte);
    return head[0] === 255 && head[1] === 216 && head[2] === 255;
  };
  const skipped = [];
  const queue = [];
  let planned = 0;
  for (const entry of listed) {
    if (!entry || typeof entry !== "object") continue;
    const name = text(entry.name) || text(entry.attachment_file_name) || "Untitled file";
    const skip = (reason) => skipped.push({ name, reason });
    const type = typeOf(entry);
    const size = Number(entry.attachment_file_size) || 0;
    if (entry.id === undefined || entry.id === null || String(entry.id) === "") skip("CHR gave it no id.");
    else if (entry.uploading === true) skip("CHR was still uploading it.");
    else if (entry.upload_error) skip("Its upload to CHR failed, so CHR holds no copy.");
    else if (!text(entry.attachment_url)) skip("CHR holds no file for it.");
    else if (!type) skip("Not a PDF or a JPEG or PNG image" + (text(entry.attachment_content_type) ? " (" + text(entry.attachment_content_type) + ")." : "."));
    else if (size > limits.fileBytes) skip("Larger than the " + megabytes(limits.fileBytes) + " one file may be.");
    else if (queue.length >= limits.files) skip("Past the " + limits.files + " files one send carries.");
    else if (planned + size > limits.totalBytes) skip("Past the " + megabytes(limits.totalBytes) + " one send carries.");
    else {
      planned += size;
      queue.push({ entry, name, type });
    }
  }
  const results = new Array(queue.length);
  let next = 0;
  let done = 0;
  let carried = 0;
  progress(0, queue.length);
  const download = async () => {
    while (next < queue.length) {
      const index = next;
      next += 1;
      const { entry, name, type } = queue[index];
      let outcome;
      try {
        const url = new URL(text(entry.attachment_url), api);
        const own = url.origin === api || url.origin === where.origin;
        const response = await get(url.href, own ? "include" : "omit", "*/*", 120000);
        if (!response.ok) outcome = { reason: "CHR did not send it (it answered " + response.status + ")." };
        else {
          const bytes = await response.arrayBuffer();
          if (bytes.byteLength === 0) outcome = { reason: "CHR sent an empty file." };
          else if (bytes.byteLength > limits.fileBytes) outcome = { reason: "Larger than the " + megabytes(limits.fileBytes) + " one file may be." };
          else if (carried + bytes.byteLength > limits.totalBytes) outcome = { reason: "Past the " + megabytes(limits.totalBytes) + " one send carries." };
          else if (!signed(type, bytes)) outcome = { reason: "CHR sent something other than the PDF or image it listed." };
          else {
            carried += bytes.byteLength;
            outcome = {
              item: {
                id: String(entry.id),
                name,
                fileName: text(entry.attachment_file_name) || name,
                mimeType: type,
                bytes,
                comment: text(entry.comment),
                subType: text(entry.sub_type),
                reportSubclass: text(entry.report_subclass),
                sourceFacility: text(entry.source_facility),
                createdAt: text(entry.created_at),
                addedAt: text(entry.date_of_added),
                receivedAt: text(entry.received_at),
                collectedAt: text(entry.earliest_collected_at),
                reviewedAt: text(entry.last_reviewed_at),
                archived: entry.archived === true,
              },
            };
          }
        }
      } catch (error) {
        outcome = { reason: "It could not be downloaded from CHR." };
      }
      results[index] = outcome.item ? outcome : { name, reason: outcome.reason };
      done += 1;
      progress(done, queue.length);
    }
  };
  await Promise.all([download(), download(), download()]);
  const items = [];
  for (const result of results) {
    if (result.item) items.push(result.item);
    else skipped.push({ name: result.name, reason: result.reason });
  }
  return { items, skipped };
}`;

/** Where an EMR shows one patient's chart, so the bookmark copies that and nothing around it. */
export interface CaptureScope {
  /** The chart, most specific first: the first one the page shows is copied. */
  readonly chart: readonly string[];
  /** That patient's forms, copied when open; `having` tells them from other dialogs. */
  readonly forms: ReadonlyArray<{ readonly select: string; readonly having: string }>;
  /** What the bookmark says on a page that shows no chart. */
  readonly missing: string;
  /** The bookmark's download of the patient's files (`bobdocFiles`), where the EMR offers one. */
  readonly files?: string;
}

export const CAPTURE_SCOPES: Readonly<Record<EmrPageSource, CaptureScope>> = {
  // The chart beside CHR's "Opened patients" list, its Update patient form, and the Update
  // identification form opened from it (the only place CHR shows a health card's expiry).
  "telus-chr": {
    chart: [".patient-chart-content-view.visible-patient-contents", ".patient-chart-content-view"],
    forms: [
      { select: ".ih-draggable-dialog", having: "form.respondent-information" },
      { select: ".q-dialog", having: "input[name='Version Code']" },
    ],
    missing: "Open the patient's chart in TELUS CHR, then click Send to BobDoc again.",
    files: CHR_FILES_SOURCE,
  },
};

/** The BobDoc route the bookmark opens to receive a capture. */
export const EMR_PAGE_RECEIVER_PATH = "/import/emr-page";

/** Messages between the bookmark (in the other EMR's page) and the BobDoc tab it opens. */
export const EMR_PAGE_MESSAGE = {
  capture: "bobdoc:emr-page-capture",
  ready: "bobdoc:emr-page-ready",
  received: "bobdoc:emr-page-received",
} as const;

/**
 * The bookmark's own script, around the capture: it captures the chart first,
 * then opens BobDoc's receiver (at once, while the click still allows a new
 * tab) and, where the EMR offers them, downloads the patient's files
 * meanwhile. It hands the chart and files over with postMessage, addressed to
 * exactly this BobDoc origin, once BobDoc says it is ready and the files are
 * in. A small status note in the other EMR's page says what happened. On a
 * page with no chart it sends nothing and says so. When BobDoc cannot be
 * reached (a blocked pop-up, or a page whose opener policy severs the new
 * tab), the note offers to copy the chart, without its files, so it can be
 * pasted into BobDoc's import box.
 */
function bookmarkletScript(origin: string, source: EmrPageSource): string {
  const scope = CAPTURE_SCOPES[source];
  const label = EMR_PAGE_SOURCE_LABELS[source];
  return `(() => {
  const ORIGIN = ${JSON.stringify(origin)};
  const SOURCE = ${JSON.stringify(source)};
  ${PAGE_CAPTURE_SOURCE}
  ${scope.files ?? "const bobdocFiles = null;"}
  const previous = document.getElementById("bobdoc-page-capture");
  if (previous) previous.remove();
  let capture;
  try {
    capture = bobdocCapturePage(document, ${JSON.stringify({ chart: scope.chart, forms: scope.forms })});
  } catch (error) {
    window.alert("BobDoc could not read this page.");
    return;
  }
  const panel = document.createElement("div");
  panel.id = "bobdoc-page-capture";
  panel.setAttribute("role", "status");
  panel.style.cssText = "position:fixed;top:16px;right:16px;z-index:2147483647;max-width:320px;padding:12px 14px;border:1px solid #cde1f6;border-radius:12px;background:#ffffff;color:#111827;font:13px/1.4 system-ui,sans-serif;box-shadow:0 8px 24px rgba(17,24,39,0.16)";
  const text = document.createElement("p");
  text.style.cssText = "margin:0";
  panel.appendChild(text);
  (document.body || document.documentElement).appendChild(panel);
  const show = (words) => {
    text.textContent = words;
  };
  const dismiss = () => setTimeout(() => panel.remove(), 8000);
  if (capture === null) {
    show(${JSON.stringify(scope.missing)});
    dismiss();
    return;
  }
  const message = {
    type: ${JSON.stringify(EMR_PAGE_MESSAGE.capture)},
    version: 1,
    source: SOURCE,
    capturedAt: new Date().toISOString(),
    html: capture.html,
  };
  const offerCopy = (reason) => {
    show(reason + " Copy the chart instead (its files stay behind), then paste it into BobDoc's import box.");
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = "Copy chart for BobDoc";
    button.style.cssText = "margin-top:8px;padding:6px 10px;border:0;border-radius:8px;background:#2468cc;color:#ffffff;font:600 13px system-ui,sans-serif;cursor:pointer";
    button.addEventListener("click", () => {
      navigator.clipboard.writeText(JSON.stringify(message)).then(
        () => {
          button.remove();
          show("Copied. In BobDoc, click the import box and press Ctrl+V.");
          dismiss();
        },
        () => show("Copying was blocked. Click Send to BobDoc again."),
      );
    });
    panel.appendChild(button);
  };
  const tab = window.open(ORIGIN + ${JSON.stringify(EMR_PAGE_RECEIVER_PATH)}, "_blank");
  if (!tab) {
    offerCopy("BobDoc didn't open; the browser may have blocked it.");
    return;
  }
  const started = Date.now();
  let watch = 0;
  let stopped = false;
  // The files, once downloaded (or why they could not be); null while that is under way.
  let files = bobdocFiles ? null : undefined;
  let ready = false;
  let sentAt = 0;
  const sending = () => {
    const count = files && files.items ? files.items.length : 0;
    return count === 0
      ? "Sending this chart to BobDoc…"
      : "Sending this chart and " + count + (count === 1 ? " file" : " files") + " to BobDoc…";
  };
  // Once BobDoc is ready and the files are in. BobDoc says it is ready every second until the
  // chart arrives, so a send waits a while before it is repeated.
  const send = () => {
    if (stopped || !ready || files === null || Date.now() - sentAt < 10000) return;
    sentAt = Date.now();
    tab.postMessage(files === undefined ? message : Object.assign({}, message, { files }), ORIGIN);
  };
  const stop = () => {
    stopped = true;
    window.removeEventListener("message", onMessage);
    clearInterval(watch);
  };
  const onMessage = (event) => {
    if (event.source !== tab || event.origin !== ORIGIN || !event.data) return;
    if (event.data.type === ${JSON.stringify(EMR_PAGE_MESSAGE.ready)}) {
      ready = true;
      send();
    }
    if (event.data.type === ${JSON.stringify(EMR_PAGE_MESSAGE.received)}) {
      stop();
      show("Sent to BobDoc.");
      dismiss();
    }
  };
  window.addEventListener("message", onMessage);
  if (files === null) {
    show(${JSON.stringify(`Bringing the patient's files from ${label}…`)});
    bobdocFiles(location, ${JSON.stringify(CAPTURED_FILE_LIMITS)}, (done, total) => {
      if (!stopped && total > 0) show(${JSON.stringify(`Bringing the patient's files from ${label}: `)} + done + " of " + total + "…");
    })
      .catch(() => ({ failed: "The patient's files could not be brought." }))
      .then((result) => {
        files = result;
        if (stopped) return;
        show(sending());
        send();
      });
  } else {
    show(sending());
  }
  watch = setInterval(() => {
    if (tab.closed) {
      stop();
      offerCopy("BobDoc closed before the chart arrived.");
    } else if (Date.now() - started > 15 * 60 * 1000) {
      stop();
      show("BobDoc didn't answer. Click Send to BobDoc again.");
      dismiss();
    }
  }, 1000);
})();`;
}

/** The `javascript:` URL a clinician drags to the bookmarks bar, for this BobDoc. */
export function bookmarkletHref(origin: string, source: EmrPageSource): string {
  return `javascript:${encodeURIComponent(bookmarkletScript(origin, source))}`;
}
